import { useParams, useSearchParams } from "react-router-dom";

import { useGetUploadSession, type OfficeInputSessionInfo } from "@wellcheck/api-client";

import { isCampaignAbortedError } from "../api/campaignAborted";

/**
 * useUploadSession — アップロードセッションを確認する。
 *
 * サーバ側 (backend/officeinput.VerifyHandler, ADR-0025) が `?t=<token>` を検証し、
 * 成功なら HttpOnly セッション Cookie を発行した上で `t` を除いた本 URL へ 303 リダイレクトする。
 * そのため本 hook は URL から token を扱わず、Cookie の有効性を
 * `GET /api/upload-session` (認証は Cookie 自動送信、credentials: same-origin) に
 * 問い合わせるだけでよい (features/respond/hooks/useRespondSession.ts と同じ設計)。
 *
 * campaignRecordId は表示・エラー文脈に使うほか、session API のレスポンスの campaignRecordId
 * と突合する (ADR-0023 追記、useRespondSession と同じ理由: 別 campaign の有効な Cookie が
 * 残っていても別案件のアップロード画面を表示しないため)。
 *
 * frontend 側の命名 (フック名・型名) はユーザー視点の操作語彙「アップロード」のまま維持する
 * (backend の officeinput package 名は domain 語彙だが、これは境界層である frontend/HTTP には
 * 及ぼさない意図的な線引き。backend/officeinput/types.go の package docstring 参照)。
 *
 * 案件キャンセル (ADR-0055 Decision 3) は `?reason=campaign_aborted` (未訪問リンク) と
 * session API の 401 + detail="campaign aborted" (verify 通過後のキャンセル) の
 * 2 経路で表現される。前者は URL だけで確定するため fetch をスキップする。
 */
export interface UploadSessionParams {
  campaignRecordId: string;
}

export type UploadSessionState =
  | { status: "loading" }
  | { status: "invalid" }
  | { status: "campaign_aborted" }
  | { status: "valid"; session: OfficeInputSessionInfo };

export function useUploadSession(): UploadSessionParams & { session: UploadSessionState } {
  const { campaignRecordId } = useParams<"campaignRecordId">();
  const [searchParams] = useSearchParams();
  const isCampaignAbortedByRedirect = searchParams.get("reason") === "campaign_aborted";

  const query = useGetUploadSession({
    query: {
      // 401 はリトライ対象外 (無効セッションを再試行しても状態は変わらない)。
      retry: false,
      select: (response) => (response.status === 200 ? response.data : undefined),
      // campaign_aborted は URL だけで終端状態が確定しているため fetch 自体をスキップする。
      enabled: !isCampaignAbortedByRedirect,
    },
  });

  const session: UploadSessionState =
    isCampaignAbortedByRedirect || isCampaignAbortedError(query.error)
      ? { status: "campaign_aborted" }
      : query.isLoading
        ? { status: "loading" }
        : query.isError || !query.data
          ? { status: "invalid" }
          : query.data.campaignRecordId !== campaignRecordId
            ? { status: "invalid" }
            : { status: "valid", session: query.data };

  return {
    campaignRecordId: campaignRecordId ?? "",
    session,
  };
}
